/**
 * Frustum → ground-plane polygon (the camera's actual ground footprint).
 *
 * Replaces the legacy 2D pie-slice rendering (`utils.ts:generateFOVPath`)
 * with a 3D-aware projection that respects mounting height, tilt, VFOV,
 * and lens-projection type.
 *
 * Per EN 62676-4 / IEC 62676-4:2025 — geometric optics. For rectilinear
 * lenses a frustum projects to a quadrilateral on the ground (or a partial
 * quad with virtual far corners when the upper edges look above horizon).
 *
 * This module is the single source of truth shared by the live designer
 * (`CameraRenderer.tsx`), DXF export (`export/dxf/entities/fov.ts`), PDF
 * export (`export/pdf/entities.ts`), and the ExportCanvas (`export/ExportCanvas.tsx`).
 * Math drift between these has been a long-standing problem
 * (`feedback_mpp_designer_vs_export`) — eliminated by routing all callers
 * through `computeGroundFootprint`.
 */

import {
  type CameraFrustum,
  type Vec3,
  cameraBasis,
  frustumCornerRays,
  groundReachM,
} from './frustum';
import { resolveCameraPovProjection, needsPovDewarp } from '../povProjection';
import { defaultImagerCount } from '@/data/panoramicImagers';
import { elevationAt, elevationSampleAt, terrainProvenanceAt } from '@/lib/terrain';
import { PERF, pcount, pstage } from '../perfDiag';
import type {
  CoverageResult,
  CoverageProvenance,
  CoverageLimit,
  CoverageDegradation,
} from './coverage';

const M_PER_DEG_LAT = 111_320;

/** Mean Earth radius (m). */
const EARTH_R_M = 6_371_000;

/**
 * Refraction coefficient for VISIBLE light and thermal IR, ~0.13.
 *
 * NOT the radio 4/3 rule. `radioLinkGeometry.ts` sits next door and uses the
 * radio figure correctly for antennas; borrowing it here would inflate every
 * optical reach by about 8 %. Air density falls with height, so a sightline
 * bends gently downward and sees slightly past the geometric horizon; folding
 * that into an enlarged Earth radius is the standard geodetic dodge.
 */
const OPTICAL_REFRACTION_K = 0.13;

/** Effective Earth radius for optical/thermal sightlines: ~7323 km. */
export const EFFECTIVE_EARTH_R_M = EARTH_R_M / (1 - OPTICAL_REFRACTION_K);

/**
 * Horizontal distance at which a ray leaving `hM` above ground with descending
 * slope `slope` (rise/run, positive downward) meets the curved Earth. Returns
 * null when it never does — the ray is shallower than the tangent to the
 * horizon and escapes.
 *
 * Ground drops s²/(2R) below the tangent plane, so
 *   h − slope·s = −s²/(2R)  →  s² − 2R·slope·s + 2Rh = 0
 * and the near root is R·slope − √(R²·slope² − 2Rh). A zero discriminant IS
 * the tangent case and lands exactly on √(2Rh), the horizon.
 *
 * The flat model this replaces (h / slope) is wrong in BOTH directions: it
 * pulls shallow hits in (30 m up at 0.5° down lands 3535 m out, not 3438 m)
 * and it invents hits for rays that should escape entirely.
 */
export function curvedGroundDistanceM(hM: number, slope: number): number | null {
  if (!(slope > 0) || !(hM > 0)) return null;
  const R = EFFECTIVE_EARTH_R_M;
  let disc = R * R * slope * slope - 2 * R * hM;
  if (disc < 0) {
    // Tangent, to floating point. Anything meaningfully below zero escapes.
    if (disc > -1e-6 * R * hM) disc = 0;
    else return null;
  }
  return R * slope - Math.sqrt(disc);
}

/** A 2D point in SVG pixel coordinates (x right, y down). */
export interface Point2D {
  x: number;
  y: number;
}

/** Lens projection type, determining how rays leave the lens.
 *
 *  rectilinear:   straight lines stay straight (tan-based). Standard CCTV
 *                 lenses (HFOV up to ~120°). This is the default and what
 *                 99% of installations use.
 *  equidistant:   r = f·θ. Fisheye / panoramic (HFOV ≥ 170°). Distance
 *                 from image centre is proportional to angle from optical
 *                 axis. Good for surveillance fisheye where uniform angular
 *                 sampling matters more than rectilinear straight lines.
 *  equisolid:     r = 2f·sin(θ/2). Another common fisheye projection.
 *                 Slightly different distortion profile, used by some
 *                 manufacturers. Numerically very close to equidistant for
 *                 our purposes.
 *
 *  fan:           N flat imagers side by side, each owning an equal slice of
 *                 the azimuth span. This is what a panoramic body physically
 *                 is, and it is the only model that produces the notch under
 *                 the camera where two imagers' near edges cross. One imager
 *                 (n = 1) reduces exactly to 'rectilinear'; the n -> infinity
 *                 limit is a perfect circle, which no device produces.
 *
 *  For non-rectilinear projections, the ground footprint is no longer a
 *  simple quadrilateral — it's a curved region. We sample the frustum
 *  border at multiple points to render a smooth polygon approximation.
 */
export { defaultImagerCount };

/** Widest span one FLAT imager is modelled as covering.
 *
 *  Past this the tangent map is both physically wrong and numerically unsafe:
 *  at 90 degrees it is infinite and beyond it changes sign, which folds the
 *  footprint back through the camera. Single-lens bodies wider than this
 *  exist and are treated as fisheyes instead. */
const WIDEST_FLAT_IMAGER_RAD = (120 * Math.PI) / 180;

export type LensProjection = 'rectilinear' | 'equidistant' | 'equisolid' | 'fan';

/**
 * Pick the projection from the HFOV alone.
 *
 * ⚠️ Prefer `lensProjectionForCamera`, which asks the classifier that already
 * owns this question for the POV. This function cannot see the SENSOR, so it
 * cannot tell a 150° strip panorama from a 150° single-sensor wide lens, and
 * those two produce different ground shapes. It stays for callers that
 * genuinely hold nothing but an angle.
 *
 * Cameras with HFOV >= 170° are essentially fisheye; below that, rectilinear.
 * Manufacturers' panoramic claims occasionally lie.
 *
 * ⚠️ The old comment here said this threshold "matches lensMath.ts
 * getVfovHalfRad". It does not, and has not for as long as that function has
 * used the equisolid map: the VERTICAL field is computed as equisolid at every
 * angle, while this returns rectilinear below 170°. Left as-is rather than
 * silently widened, because changing it moves every wide-lens footprint in the
 * catalogue and that is its own piece of work, but the claim of agreement is
 * removed so nobody relies on it.
 */

export function defaultLensProjection(hfovDeg: number): LensProjection {
  return hfovDeg >= 170 ? 'equidistant' : 'rectilinear';
}

/** Result of a single ray's interaction with the ground plane. */
interface GroundHit {
  /** Did the ray hit the ground plane (z=0) within positive distance? */
  hits: boolean;
  /** SVG pixel coordinates of the hit (or clamped virtual far point). */
  point: Point2D;
}

/** Intersect one ray with z=0 ground plane.
 *
 *  Camera origin is at world (cam.x, cam.y_north, cam.h) where cam.y_north
 *  is the world-frame north coord (= -cam.y_svg). The ray points in
 *  direction `dir` (world frame). Hit point at t solving cam.h + t·dir.up = 0.
 *
 *  Two cases yield the same on-screen treatment:
 *
 *  1. Ray points down (dir.up < 0) AND hit distance ≤ renderRangeM:
 *     return the actual ground intersection.
 *
 *  2. Ray points up/horizontal, OR ray hits ground BEYOND renderRangeM:
 *     clamp to a virtual far point at renderRangeM along the ray's horizontal
 *     projection. Without this, rays just below horizon (where t = h / |dir.up|
 *     blows up) project to wildly distant ground points and create stray
 *     lines connecting the polygon's "above-horizon" edge to its true ground
 *     footprint — visible in CameraRenderer as a triangular shard sticking
 *     out toward the horizon.
 */
function rayGroundHit(
  cam: CameraFrustum,
  dir: Vec3,
  clampDistanceM: number,
): GroundHit {
  const horizMag = Math.hypot(dir.east, dir.north);

  // Downward ray → a flat-ground intersection always exists. Terrain (if any)
  // refines WHERE the ray actually lands via the line-of-sight march.
  // (A previous attempt to introduce a "shallow downward = clamp to range"
  // threshold broke wide-FOV / low-tilt cones where BOTH frustum edges land
  // in the shallow-downward zone — the polygon degenerated to a line.
  // Geometric ground hit is the correct visualization regardless of FOV.)
  if (dir.up < -1e-9) {
    // Curved Earth, not a plane. `curvedGroundDistanceM` returns null when the
    // ray is shallower than the tangent to the horizon and never lands — which
    // is how the horizon stops being a special case and simply falls out of the
    // geometry. Straight down (no horizontal component) keeps the plane solve;
    // there is no curvature along a vertical drop.
    const slope = horizMag > 1e-9 ? -dir.up / horizMag : Infinity;
    const sCurvedM = horizMag > 1e-9 ? curvedGroundDistanceM(cam.h, slope) : 0;
    if (sCurvedM === null) {
      // Escapes over the horizon → treat exactly like an upward ray, which the
      // block below already handles (terrain may still catch it).
    } else {
    const t0 = horizMag > 1e-9 ? sCurvedM / horizMag : cam.h / -dir.up;
    let eastOffsetM = t0 * dir.east;
    let northOffsetM = t0 * dir.north;

    const terrainEnabled = cam.terrain?.enabled === true && cam.lat !== undefined && cam.lng !== undefined;
    if (terrainEnabled && horizMag > 1e-9) {
      // Terrain-aware coverage distance: nearer of the line-of-sight hit and
      // the look-up horizon (where ground rises to eye height). -1 = terrain
      // imposes no limit within range — a downward ray over DOWNHILL ground
      // never catches the falling terrain, so it sees far → clamp to range
      // (NOT the flat solve, which would under-shoot the downhill reach).
      const reach = terrainCoverageDistM(cam, dir, clampDistanceM);
      const s = reach.distM >= 0 ? reach.distM : clampDistanceM;
      eastOffsetM = (dir.east / horizMag) * s;
      northOffsetM = (dir.north / horizMag) * s;
    }

    const horizDistM = Math.hypot(eastOffsetM, northOffsetM);

    if (horizDistM <= clampDistanceM) {
      return {
        hits: true,
        point: {
          x: cam.x + eastOffsetM * cam.pixelsPerMeter,
          // SVG y is south-positive, world north is +y, so map north→south flip:
          y: cam.y - northOffsetM * cam.pixelsPerMeter,
        },
      };
    }

    // Hit exists but beyond render range — clamp to renderRangeM in the
    // ray's horizontal direction. This prevents the "stray line" jump
    // between near-horizon ground hits and clamped above-horizon rays.
    const scale = clampDistanceM / horizDistM;
    return {
      hits: true,
      point: {
        x: cam.x + eastOffsetM * scale * cam.pixelsPerMeter,
        y: cam.y - northOffsetM * scale * cam.pixelsPerMeter,
      },
    };
    }
  }

  // Level or upward ray. On flat ground this points at the sky (no ground
  // hit) — but RISING terrain can still block it (low camera aimed at a
  // hillside), and the look-up horizon caps it where the ground climbs to eye
  // height. A clear ray over flat/low terrain returns -1 → falls through to
  // the render-range clamp below (so a tall mast still sees far).
  const upTerrain = cam.terrain?.enabled === true && cam.lat !== undefined && cam.lng !== undefined;
  if (horizMag > 1e-9 && upTerrain) {
    const reach = terrainCoverageDistM(cam, dir, clampDistanceM);
    if (reach.distM >= 0) {
      const eastUnit = dir.east / horizMag;
      const northUnit = dir.north / horizMag;
      return {
        // Only a real line-of-sight contact counts as ground. A sky ray capped
        // at the eye-height horizon still gets a point (so the polygon keeps a
        // sane far edge) but must not make the footprint look grounded.
        hits: reach.grounded,
        point: {
          x: cam.x + eastUnit * reach.distM * cam.pixelsPerMeter,
          y: cam.y - northUnit * reach.distM * cam.pixelsPerMeter,
        },
      };
    }
  }

  // No terrain intercept — clamp to render-range along the ray's horizontal
  // projection (sky ray over flat / clear-of-ridge terrain).
  if (horizMag < 1e-9) {
    // Pathological: ray points straight up. Place the virtual far point
    // directly forward of the camera (use camera's pan as fallback).
    const panRad = (cam.panDeg * Math.PI) / 180;
    return {
      hits: false,
      point: {
        x: cam.x + Math.sin(panRad) * clampDistanceM * cam.pixelsPerMeter,
        y: cam.y - Math.cos(panRad) * clampDistanceM * cam.pixelsPerMeter,
      },
    };
  }

  const eastUnit = dir.east / horizMag;
  const northUnit = dir.north / horizMag;
  return {
    hits: false,
    point: {
      x: cam.x + eastUnit * clampDistanceM * cam.pixelsPerMeter,
      y: cam.y - northUnit * clampDistanceM * cam.pixelsPerMeter,
    },
  };
}

/** Terrain-aware horizontal coverage distance (metres) for a camera ray.
 *  Returns the NEARER of two limits, or -1 when terrain imposes neither within
 *  range (caller falls back to the flat-ground solve / render-range clamp):
 *
 *   1. LINE OF SIGHT — a DDA march steps the ray and stops where rising ground
 *      first meets it (a near slope / ridge / bank the camera can't see past).
 *      Direction-agnostic: rising ground intercepts level and UPWARD rays too,
 *      not only downward ones (a low camera aimed at a hillside).
 *
 *   2. LOOK-UP HORIZON — a ground-based camera sees the ground only out to
 *      where the terrain climbs to its EYE height (cam.h above its own base).
 *      Beyond that the ground sits above eye level, so the camera is looking
 *      UP at it: grazing, not real coverage. This is what keeps a 1 m camera
 *      on a slope SHORT, while a tall mast — or ANY camera over flat ground
 *      (terrain never reaches eye height → this limit never triggers) — still
 *      sees far. It scales naturally with mounting height.
 *
 *  Buildings are NOT in the DEM by design — model them as obstacles. */
interface TerrainReach {
  /** Distance to use for the point, or -1 for "terrain imposes no limit". */
  distM: number;
  /** True only for a real line-of-sight contact with the ground. The
   *  eye-height horizon is a cap on a ray that sees SKY, so it is not one. */
  grounded: boolean;
  /** Distance at which the march walked off every measured/modelled elevation
   *  source and started reading the flat `referenceElevationM` fallback, or -1
   *  if it never did. Beyond it the answer is an assumption about a plateau at
   *  the camera's own site elevation, not a reading — worth surfacing rather
   *  than presenting with the same confidence as the rest. */
  assumedFromM: number;
}

/**
 * Memo for the DEM march below, and the reason the 2D plan can afford terrain.
 *
 * ── The cost it removes ─────────────────────────────────────────────────────
 *
 * The march walks up to ~300 elevation samples PER RAY, and a footprint ring is
 * 4 × `samplesPerEdge` rays, so a rectilinear camera costs up to ~9 600 DEM
 * reads. That is paid again from scratch on every finished pan or zoom, because
 * the ring is rebuilt whenever the map's pixel projection changes. Measured on a
 * real customer project — 51 cameras, their own DEM — the footprint alone cost
 * 34 ms per gesture with terrain against 4 ms without, and 139 ms at a 4×-slower
 * CPU against 13 ms. A ~10× multiplier, on top of the FOV clip.
 *
 * ── Why memoising is exact, not an approximation ────────────────────────────
 *
 * Every input to the march is in METRES and none of them is a pixel: the camera
 * site (`lat`/`lng`), its height, the ray direction, the clamp distance
 * (`groundReachM(renderRangeM, h)`) and the terrain model. Panning and zooming
 * change only `pixelsPerMeter` and the apex, which are applied AFTER this
 * returns. So across a pan the march is asked the identical question and must
 * give the identical answer — the cache just stops it recomputing it.
 *
 * Keyed by the terrain object's IDENTITY, which is what makes invalidation
 * automatic: `TerrainModel` is plain data replaced wholesale by `setTerrain`
 * (never mutated in place — checked), so new elevations mean a new object and an
 * empty cache. Moving a camera, changing its height or its range all change the
 * key. A WeakMap lets a discarded terrain take its entries with it.
 *
 * ── The shape, arrived at by measurement ────────────────────────────────────
 *
 * Two levels, because the two halves of the key behave differently. Sites — a
 * camera's position, height and range — are FEW, one per camera, so a flat scan
 * comparing raw numbers finds one without touching a hash or allocating. The
 * DIRECTION within a site is not few: the footprint samples every edge and the
 * ray solver asks twice per sample, so it runs to hundreds, and there a scan
 * turns quadratic. That is not a guess — the all-scan version measured 158 ms on
 * the customer's project at a 4×-slower CPU, worse than the 46 ms of a version
 * keyed on a seven-field string. So directions get a Map, keyed on the three
 * numbers that identify them rather than the seven the whole call needs.
 */
const terrainReachCache = new WeakMap<object, TerrainReachSite[]>();
/** One camera site: everything the march depends on apart from the direction. */
interface TerrainReachSite {
  lat: number;
  lng: number;
  h: number;
  clampM: number;
  byDir: Map<string, TerrainReach>;
}
/** Distinct camera sites, and directions per site, before the cache is dropped
 *  and refilled. Both sit far above a real project; the caps exist only so a
 *  long editing session — where every nudge of a camera mints a new site —
 *  cannot grow this without bound. */
const TERRAIN_REACH_MAX_SITES = 4_000;
const TERRAIN_REACH_MAX_DIRS = 8_000;

function terrainCoverageDistM(
  cam: CameraFrustum,
  dir: Vec3,
  clampDistanceM: number,
): TerrainReach {
  const terrainEnabled = cam.terrain?.enabled === true && cam.lat !== undefined && cam.lng !== undefined;
  if (!terrainEnabled) return { distM: -1, grounded: false, assumedFromM: -1 };

  const terrainKey = cam.terrain as unknown as object;
  let sites = terrainReachCache.get(terrainKey);
  if (!sites) {
    sites = [];
    terrainReachCache.set(terrainKey, sites);
  }
  const lat = cam.lat!;
  const lng = cam.lng!;
  let site: TerrainReachSite | undefined;
  for (let i = 0; i < sites.length; i++) {
    const s = sites[i];
    if (s.lat === lat && s.lng === lng && s.h === cam.h && s.clampM === clampDistanceM) {
      site = s;
      break;
    }
  }
  if (!site) {
    if (sites.length >= TERRAIN_REACH_MAX_SITES) sites.length = 0;
    site = { lat, lng, h: cam.h, clampM: clampDistanceM, byDir: new Map() };
    sites.push(site);
  }
  // Doubles round-trip through `toString` exactly, so identical directions build
  // an identical key and a hit can only return what the march would have.
  const dirKey = `${dir.east}|${dir.north}|${dir.up}`;
  const hit = site.byDir.get(dirKey);
  if (hit) { if (PERF) pcount('teren: cache HIT'); return hit; }

  if (PERF) pcount('teren: cache MISS (marsz DEM)');
  const reach = PERF
    ? pstage('teren: marsz DEM', () => terrainCoverageMarch(cam, dir, clampDistanceM))
    : terrainCoverageMarch(cam, dir, clampDistanceM);
  if (site.byDir.size >= TERRAIN_REACH_MAX_DIRS) site.byDir.clear();
  site.byDir.set(dirKey, reach);
  return reach;
}

/** The march itself. Call `terrainCoverageDistM` — it memoises this. */
function terrainCoverageMarch(
  cam: CameraFrustum,
  dir: Vec3,
  clampDistanceM: number,
): TerrainReach {
  const horizMag = Math.hypot(dir.east, dir.north);
  if (horizMag <= 1e-9) return { distM: -1, grounded: false, assumedFromM: -1 }; // straight up/down

  const camBaseElevM = elevationAt(cam.terrain, cam.lat!, cam.lng!);
  const metersPerDegLng = M_PER_DEG_LAT * Math.cos((cam.lat! * Math.PI) / 180);
  const eastUnit = dir.east / horizMag;
  const northUnit = dir.north / horizMag;
  const vertRate = dir.up / horizMag; // ray rise per horizontal metre (<0 descending, >0 ascending)
  // Cap the step count so very long ranges stay cheap (~300 samples).
  const stepM = Math.max(2, clampDistanceM / 300);
  let prevClearance = cam.h; // at the camera the ray sits cam.h above the base
  let prevDelta = 0;
  let prevS = 0;
  let eyeHorizonM = -1; // distance where terrain first reaches eye height
  let assumedFromM = -1; // distance where measured elevation ran out

  for (let s = stepM; s <= clampDistanceM; s += stepM) {
    const lat = cam.lat! + (northUnit * s) / M_PER_DEG_LAT;
    const lng = cam.lng! + (eastUnit * s) / metersPerDegLng;
    const sample = elevationSampleAt(cam.terrain, lat, lng);
    if (assumedFromM < 0 && sample.source === 'reference') assumedFromM = s;
    // Curvature folded in as an effective drop: the ground falls s²/(2R) below
    // the camera's tangent plane, so distant terrain sits lower than its DEM
    // elevation says relative to here. One term, and the horizon, the reach of
    // a shallow ray and the block by a far ridge all come out right together.
    const terrainDeltaM =
      sample.elevM - camBaseElevM - (s * s) / (2 * EFFECTIVE_EARTH_R_M);

    // (2) Look-up horizon: first crossing where terrain reaches eye height.
    // Used ONLY as the fallback when the ray never meets the ground at all
    // (it climbs clear of the slope and sees sky). It must NOT shorten a ray
    // that does meet the ground — see the return below.
    if (eyeHorizonM < 0 && terrainDeltaM >= cam.h) {
      const denom = terrainDeltaM - prevDelta;
      const frac = denom > 1e-9 ? (cam.h - prevDelta) / denom : 0;
      eyeHorizonM = prevS + frac * (s - prevS);
    }

    // (1) Line-of-sight hit: ray meets the rising ground.
    const rayZ = cam.h + vertRate * s;       // ray height above camera base at distance s
    const clearance = rayZ - terrainDeltaM;  // >0 ray clears terrain, <=0 ray meets/is blocked
    if (clearance <= 0) {
      const denom = prevClearance - clearance;
      const frac = denom > 1e-9 ? prevClearance / denom : 0;
      const hitS = prevS + frac * (s - prevS);
      // The line-of-sight hit is the answer, full stop. This used to return
      // min(hitS, eyeHorizonM), which cut a camera aimed UP a slope back to
      // where the ground merely reached its eye height — a 9.8 m mast on a 16%
      // slope was capped at 61 m although its upper edge still cleared the
      // ground by 7.7 m there and only met it at 280 m. Eye height stops
      // mattering the moment the ray outruns it; the march already knows where
      // the ground actually blocks the view.
      return { distM: hitS, grounded: true, assumedFromM };
    }

    prevClearance = clearance;
    prevDelta = terrainDeltaM;
    prevS = s;
  }
  // No line-of-sight hit within range: this ray sees SKY over the terrain.
  // The eye-height horizon still caps where the point is drawn, so the ray does
  // not paint a stripe all the way out to render range — but it is NOT ground
  // contact, and saying otherwise is what let a camera aimed up a mountain
  // build a footprint out of rays that never touch anything. Flagged
  // accordingly so computeGroundFootprint can tell a real hit from this.
  return { distM: eyeHorizonM, grounded: false, assumedFromM };
}

/** Sample a ray at a parametric (sH, sV) inside the frustum's normalised
 *  image plane and return its ground hit. Used for sampling lens-projection
 *  edges (fisheye) and DORI iso-curves.
 *
 *  sH ∈ [-1, +1]: horizontal position in normalised image (-1 = left edge).
 *  sV ∈ [-1, +1]: vertical position (-1 = bottom of frame).
 *  projection: rectilinear maps via tan; equidistant via linear angle.
 */
export function projectImagePoint(
  cam: CameraFrustum,
  sH: number,
  sV: number,
  projection: LensProjection,
): GroundHit {
  const basis = cameraBasis(cam.panDeg, cam.tiltDeg, cam.rollDeg);
  const hfovHalfRad = (cam.hfovDeg * Math.PI) / 360;
  const vfovHalfRad = (cam.vfovDeg * Math.PI) / 360;

  let dir: Vec3;
  if (projection === 'fan') {
    // A panoramic body is N IMAGERS side by side, so its footprint is the
    // union of N frusta and not one giant frustum. The ray is therefore built
    // in the OWNING imager's own frame.
    //
    // Both single-frustum readings of a panorama are wrong, in opposite
    // directions, and both were reported from production on 2026-08-13:
    //   - One flat sensor across the whole span puts the top of the frame
    //     higher at the flanks (the same effect that puts more sky in the
    //     corners of a wide photograph), so the boundary runs away at the
    //     sides and curves back in the middle: "zakonczenie fov jak U".
    //   - Sweeping one declination around the vertical axis is the N -> oo
    //     limit, where every azimuth is identical and the boundary is a
    //     perfect circle: "piekne kolko, ktore nie ma nic do symulacji".
    // A real device sits between the two: each imager contributes its own
    // curved edge, and where two imagers meet their near edges cross, leaving
    // the notch under the camera that an installer actually sees.
    const n = Math.max(1, Math.round(cam.imagerCount ?? defaultImagerCount(cam.hfovDeg)));
    const halfSpanRad = hfovHalfRad;
    const perSpanRad = (2 * halfSpanRad) / n;
    // Azimuth is linear in sH across the whole body: the imagers are laid out
    // by ANGLE, each owning an equal slice.
    const psiRad = sH * halfSpanRad;
    const idx = Math.min(n - 1, Math.max(0, Math.floor((psiRad + halfSpanRad) / perSpanRad)));
    const centreRad = -halfSpanRad + perSpanRad * (idx + 0.5);
    // Yawed about the WORLD vertical, with every imager carrying the body's
    // own tilt. That is the arrangement the hardware shows: in real footage
    // from a 180 deg body the near paving is visible across the FULL width of
    // the frame, which only happens if every imager looks down by the same
    // amount. Fanning about the tilted housing axis instead leaves the flank
    // imagers near level, and they then see distance where the device sees
    // ground; it was tried on 2026-08-13 and rejected against that footage.
    // See geometry/fanPose.ts for the rejected construction and why the
    // compositor made it tempting.
    // ⛔ NO ROLL ON THIS PATH, and it is deliberate rather than forgotten.
    //
    // The arrangement above was settled against real footage of a TILTED body.
    // Nobody has filmed a ROLLED one, and the two readings differ: the fan axis
    // is fixed in the housing, so rolling the housing should roll the axis with
    // it, which contradicts the world-vertical fan this line encodes. Applying
    // roll here would be picking one of those readings with no evidence.
    //
    // So a fan body is denied roll at the source — see `effectiveRollDeg`, which
    // returns 0 for exactly these cameras — and this literal is the second lock
    // on the same door.
    const localBasis = cameraBasis(cam.panDeg + (centreRad * 180) / Math.PI, cam.tiltDeg, 0);
    const thetaH = psiRad - centreRad;
    if (perSpanRad <= WIDEST_FLAT_IMAGER_RAD) {
      // Inside its own slice the imager is an ordinary flat sensor, so the
      // image plane maps x = tan(local azimuth), exactly as rectilinear does.
      const tanH = Math.tan(thetaH);
      const tanV = Math.tan(vfovHalfRad) * sV;
      dir = {
        east: localBasis.forward.east + tanH * localBasis.right.east + tanV * localBasis.upCam.east,
        north: localBasis.forward.north + tanH * localBasis.right.north + tanV * localBasis.upCam.north,
        up: localBasis.forward.up + tanH * localBasis.right.up + tanV * localBasis.upCam.up,
      };
    } else {
      // One imager asked to cover this much is not a flat sensor behind a
      // rectilinear lens, it is a fisheye, and the tangent above would run
      // away and fold the footprint back on itself past 90 degrees. Hikvision
      // publishes exactly such a body: DS-2CD2387G3P-LIS2UY is "Fixed focal
      // lens, 2.8 mm, horizontal FOV 182.6" on ONE 1/2.5" sensor. Angles are
      // therefore applied directly, which is the equidistant behaviour and
      // stays finite at any span.
      const thetaV = sV * vfovHalfRad;
      const cH = Math.cos(thetaH);
      const sHa = Math.sin(thetaH);
      const cV = Math.cos(thetaV);
      const sVa = Math.sin(thetaV);
      dir = {
        east: localBasis.forward.east * cH * cV + localBasis.right.east * sHa * cV + localBasis.upCam.east * sVa,
        north: localBasis.forward.north * cH * cV + localBasis.right.north * sHa * cV + localBasis.upCam.north * sVa,
        up: localBasis.forward.up * cH * cV + localBasis.right.up * sHa * cV + localBasis.upCam.up * sVa,
      };
    }
  } else if (projection === 'rectilinear') {
    const tanH = Math.tan(hfovHalfRad) * sH;
    const tanV = Math.tan(vfovHalfRad) * sV;
    dir = {
      east: basis.forward.east + tanH * basis.right.east + tanV * basis.upCam.east,
      north: basis.forward.north + tanH * basis.right.north + tanV * basis.upCam.north,
      up: basis.forward.up + tanH * basis.right.up + tanV * basis.upCam.up,
    };
  } else {
    // equidistant: angle from optical axis is linear in sH/sV
    // For a frustum, treat the image plane radially: full angular extent at
    // (sH=±1, sV=0) is hfovHalf, at (sH=0, sV=±1) is vfovHalf. Mix them as
    // an ellipse for off-axis points (this is an approximation matching
    // standard sensor-radial angle mapping for fisheye lenses).
    const angleFromAxis = Math.sqrt(
      (sH * hfovHalfRad) ** 2 + (sV * vfovHalfRad) ** 2,
    );
    const azimuthInImage = Math.atan2(sH * hfovHalfRad, sV * vfovHalfRad);
    // Scene angle from image position. Equidistant (r = f·θ) is linear: the
    // scene angle IS angleFromAxis. Equisolid (r = 2f·sin(θ/2)) needs the
    // INVERSE map so the frame edge still reaches the full field angle:
    //   ρ = angleFromAxis / edgeAngle   (normalised image radius, 0..1)
    //   θ = 2·asin(ρ·sin(edgeAngle/2))  → edge (ρ=1) → edgeAngle exactly.
    let angularRadius: number;
    if (projection === 'equisolid') {
      const m = Math.max(Math.abs(sH), Math.abs(sV)) || 1;
      const edgeAngle = Math.sqrt(
        ((sH / m) * hfovHalfRad) ** 2 + ((sV / m) * vfovHalfRad) ** 2,
      );
      const rho = edgeAngle > 1e-9 ? angleFromAxis / edgeAngle : 0;
      angularRadius = 2 * Math.asin(Math.min(1, rho * Math.sin(edgeAngle / 2)));
    } else {
      angularRadius = angleFromAxis;
    }
    // Tilt direction in image plane: image up = upCam, image right = right.
    // angularRadius is the half-angle from optical axis; project via cos/sin.
    const cosTheta = Math.cos(angularRadius);
    const sinTheta = Math.sin(angularRadius);
    const aH = sinTheta * Math.sin(azimuthInImage);
    const aV = sinTheta * Math.cos(azimuthInImage);
    dir = {
      east: cosTheta * basis.forward.east + aH * basis.right.east + aV * basis.upCam.east,
      north: cosTheta * basis.forward.north + aH * basis.right.north + aV * basis.upCam.north,
      up: cosTheta * basis.forward.up + aH * basis.right.up + aV * basis.upCam.up,
    };
  }

  // Range is a distance from the LENS, so its ground reach is √(R²−h²).
  // See `groundReachM`.
  return rayGroundHit(cam, dir, groundReachM(cam.renderRangeM, cam.h));
}

/** Compute the camera's ground footprint as a polygon (counter-clockwise
 *  in SVG screen coords; matches default SVG fill convention).
 *
 *  Rectilinear lens: 4-corner quadrilateral, where each corner is either
 *    a) the lower-edge ray's ground hit, OR
 *    b) the upper-edge ray clamped at render range (when looking above
 *       horizon, common for low-tilt high-mounted cameras).
 *
 *  Side edges (left/right at the same vertical position) get subdivided
 *  if the angular span is large, to give a smoother polygon outline. For
 *  rectilinear straight-line edges 2 endpoints are exact.
 *
 *  Fisheye / equidistant lens: edges are sampled at SAMPLES_PER_EDGE
 *  points to approximate the curved boundary smoothly.
 *
 *  Returns an ordered list of pixel-coordinate points forming the polygon.
 *  Caller renders as SVG path `M x0 y0 L x1 y1 ... L xn yn Z`.
 */
/** Samples per footprint edge — straight rectilinear edges need few, curved
 *  fisheye boundaries need many. Shared with `footprintNearEdge` so the
 *  near-edge slice can never disagree with how the ring was built. */
function samplesPerEdge(projection: LensProjection): number {
  return projection === 'rectilinear' ? 8 : 32;
}

/**
 * The projection a PLACED camera should be simulated with.
 *
 * Delegates to the POV classifier rather than re-deciding from the angle, so
 * the map, the 3D view, the PDF and the DXF cannot disagree about what kind of
 * camera this is. That disagreement was real and visible: the classifier calls
 * a strip sensor a panorama from 150°, while `defaultLensProjection` only left
 * `rectilinear` at 170°, so between those two a body was previewed as a
 * panorama and drawn on the map as an ordinary wide lens.
 *
 * A nadir dome keeps a fisheye model: its field is a downward hemisphere, not
 * a horizontal fan, so a row of side-by-side imagers does not describe it.
 */
export function lensProjectionForCamera(
  camera: Parameters<typeof resolveCameraPovProjection>[0],
): LensProjection {
  const res = resolveCameraPovProjection(camera);
  if (res.projection === 'panorama-180' || res.projection === 'panorama-360') {
    return 'fan';
  }
  if (res.axis === 'nadir') return 'equidistant';
  return defaultLensProjection(res.lensHfovDeg);
}

/**
 * The roll a placed camera may actually be simulated with, in degrees.
 *
 * THE ONE GATE. Every surface that poses a camera reads its roll from here, so
 * the rule below is stated once and cannot be half-applied by a caller that did
 * not know about it. It is also what keeps the map and the POV from disagreeing:
 * both ask this function, so a body cannot be drawn rolled on the plan and level
 * through the lens.
 *
 * The rule: roll is simulated only for a body whose picture IS its sensor frame.
 * Everything `needsPovDewarp` picks out is refused, for one reason in two
 * shapes — we would be asserting an arrangement nobody has evidence for.
 *
 *   FAN bodies. Their imagers sit about the world vertical, which is what real
 *   footage of a TILTED 180° body showed on 2026-08-13 (see `projectImagePoint`
 *   and `geometry/fanPose.ts`). That footage settles tilt and says nothing about
 *   a rolled housing, and the candidate answers genuinely differ: a fan axis
 *   fixed in the housing rolls with it and stops being vertical.
 *
 *   DEWARPED bodies. What the operator sees is a virtual window the vendor's
 *   dewarp produced, not the raw sensor. Whether that dewarp carries a rolled
 *   housing through or levels it out is the vendor's choice and is not
 *   documented anywhere we can read.
 *
 * That leaves the ordinary lens on a rigid mount, which is ~98.6% of the
 * catalogue and where roll is plain rigid-body geometry and exact.
 *
 * ⚠️ Returns 0 for every camera today: nothing writes `Camera.rollDeg` yet. The
 * dead-zone closed form still assumes a level sensor (see `cameraBasis`), so
 * wire the UI only once that is replaced.
 */
export function effectiveRollDeg(
  camera: Parameters<typeof resolveCameraPovProjection>[0] & { rollDeg?: number },
): number {
  const roll = camera.rollDeg;
  if (!roll || !Number.isFinite(roll)) return 0;
  return needsPovDewarp(resolveCameraPovProjection(camera)) ? 0 : roll;
}

/**
 * How the housing is turned, as ONE angle, for the UI to show and take.
 *
 * ── The model, and why it is two fields rather than one ────────────────────
 *
 * Turning a sensor 90° is exactly swapping its axes. That is measured, not
 * assumed: a corridor frame and a frame rolled 90° produce the same ground
 * footprint to the centimetre, and `corridorContract.test.ts` holds the numbers.
 *
 * So a turned sensor decomposes into a whole number of QUARTER TURNS plus a
 * REMAINDER of at most 45°, exactly, because rolling 90 and then φ is rolling
 * 90 + φ. The project stores the two pieces separately, and they are orthogonal
 * rather than redundant:
 *
 *   `corridorMode`  the quarter turn. Feeds `getVfovHalfRad`,
 *                   `getDisplayedHfovDeg` and `effectiveHorizontalResolution`
 *                   as it always has, so every number those produce, and all
 *                   151 places that ask them, are untouched.
 *   `rollDeg`       the remainder, in [-45, 45], handed to `cameraBasis`.
 *
 * Storing the composed angle instead would have forced all 151 sites to derive
 * the swap themselves, and a single one missed is a corridor camera that quietly
 * stops being narrowed. It also cannot work: `getDisplayedHfovDeg` has no tilt
 * parameter, and the azimuth a turned frame covers depends on tilt.
 *
 * A legacy corridor project therefore needs no migration and no rewrite. It has
 * the quarter turn already and no remainder, which is the arrangement it has
 * always had.
 *
 * ── This function ──────────────────────────────────────────────────────────
 *
 * The panel shows ONE number and its slider sets ONE number, so it composes for
 * display and `decomposeRollDeg` splits again for storage. Neither belongs in
 * the geometry, which only ever wants the remainder.
 */
export function composedRollDeg(camera: { rollDeg?: number; corridorMode?: boolean }): number {
  const residual = Number.isFinite(camera.rollDeg) ? (camera.rollDeg as number) : 0;
  const raw = (camera.corridorMode ? 90 : 0) + residual;
  // Folded into (-90, 90], and this is not cosmetics — without it the slider
  // JUMPS. `corridorMode` is a boolean, so it can only carry +90, never -90.
  // A user dragging to -46 therefore stores {corridor: true, remainder: +44},
  // which composes to +134, and the control they are still holding leaps from
  // -45 to 134. Reported 2026-08-14 and it is a genuine defect, not a display
  // nicety.
  //
  // Folding is lossless because a frame rolled 180° is centrally symmetric with
  // itself: -46 and 134 ARE the same camera, so choosing the representative
  // inside the slider's own range throws nothing away. 90 stays 90 rather than
  // becoming -90, so a corridor mount keeps the number people recognise.
  const folded = (((raw + 90) % 180) + 180) % 180 - 90;
  return folded === -90 ? 90 : folded;
}

/** Inverse of `composedRollDeg`: what to store for a slider reading. */
export function decomposeRollDeg(totalDeg: number): { corridorMode: boolean; rollDeg: number } {
  if (!Number.isFinite(totalDeg)) return { corridorMode: false, rollDeg: 0 };
  const quarters = Math.round(totalDeg / 90);
  return {
    // A HALF turn comes back unswapped, and that is right rather than lucky: a
    // frame rolled 180° is centrally symmetric with itself, so it sees the very
    // same set of directions as an upright one.
    corridorMode: Math.abs(quarters) % 2 === 1,
    rollDeg: totalDeg - 90 * quarters,
  };
}

/**
 * The near boundary of a footprint ring: its first `samplesPerEdge` points
 * (the bottom image edge, left to right) plus the bottom-right corner that
 * opens the right edge. This is the dead-zone boundary — a straight ground
 * line for a rectilinear lens, a curve for fisheye — and callers drawing the
 * dead-zone indicator take it from here instead of approximating with a
 * constant-radius arc, which understated the zone at the cone's flanks.
 *
 * Only valid for rings from the NORMAL footprint branch. The wraps-past-nadir
 * branch lays samples out differently, but there the camera sees straight
 * down, the dead zone is zero, and no indicator is drawn at all.
 */
export function footprintNearEdge(ring: Point2D[], projection: LensProjection): Point2D[] {
  const n = samplesPerEdge(projection);
  return ring.length > n ? ring.slice(0, n + 1) : ring.slice();
}

interface FootprintOutcome {
  ring: Point2D[];
  limits: CoverageLimit[];
  degraded: CoverageDegradation[];
}

function buildFootprint(
  cam: CameraFrustum,
  projection: LensProjection = defaultLensProjection(cam.hfovDeg),
): FootprintOutcome {
  const SAMPLES_PER_EDGE = samplesPerEdge(projection);
  const terrainOn = cam.terrain?.enabled === true;

  // The nearest ground the frame reaches can be FARTHER than the range.
  //
  // Tilt the camera up until its bottom edge sits just under the horizon and
  // d_near = h / tan(edge) runs away: at 4 m up, a bottom edge 0.5° below
  // horizontal first touches ground 458 m out. With a 30 m range there is no
  // coverage at all, yet every ray still reports a hit "beyond render range"
  // and gets clamped to 30 m, drawing a wide arc at the range edge for ground
  // the camera never reaches.
  //
  // Skipped when the near edge is at or past 90°, where it points straight down
  // or behind, and when terrain is on, where rising ground can put real
  // coverage much closer than the flat solve says.
  const nearEdgeDeg = cam.tiltDeg + cam.vfovDeg / 2;
  if (!terrainOn && nearEdgeDeg > 0 && nearEdgeDeg < 90) {
    const dNearM = cam.h / Math.tan((nearEdgeDeg * Math.PI) / 180);
    if (dNearM > cam.renderRangeM) {
      return {
        ring: [],
        limits: [{ kind: 'frame', atM: dNearM }],
        degraded: [{
          reason:
            `nearest ground in frame is ${Math.round(dNearM)} m away, past the ` +
            `${Math.round(cam.renderRangeM)} m range`,
        }],
      };
    }
  }

  // ── The far boundary is per COLUMN, not the top of the frame ──────────────
  //
  // The old sampler walked the image border and assumed the footprint is the
  // polygon through those points. That holds while the image→ground mapping is
  // well behaved: a camera aimed down at ground below it. It fails wherever the
  // mapping stretches, and it stretches without bound near the horizon and near
  // the terrain slope. Measured on a real mountain site: 5.1 m up, aimed 34°
  // above horizontal, over a 20% hillside, a ray at 4.65° lands 43 m out and a
  // ray at 10° lands 212 m out. Six degrees of frame, 170 m of ground. Eight
  // samples per edge cannot describe that, so the polygon came out as spikes.
  //
  // So the top of the footprint is not the top of the frame. It is the highest
  // ray in each column that still reaches real ground, found by bisection on
  // `hits`. Below that ray everything lands; above it the column is sky. The
  // bottom edge stays the near boundary, as it always was.
  //
  // Cost is controlled: the common case (a camera aimed down, whole frame on
  // the ground) answers on the first probe at sV = +1 and never bisects.
  // One precondition: the bottom of the frame must be its most DOWNWARD ray,
  // which is what makes `hits` monotonic in sV and lets bisection work. A frame
  // that reaches past straight down breaks it — a 180° fisheye aimed at nadir
  // has both its top and bottom edges on the horizon and everything downward in
  // between — so those keep the plain uniform border walk.
  // No `hits` check on this path: nearEdgeDeg >= 90 means the frame contains
  // straight-down rays by construction, so there IS ground under it even when
  // every BORDER sample sits on the horizon, which is exactly the nadir
  // fisheye's shape.
  const wrapsPastNadir = nearEdgeDeg >= 90;
  if (wrapsPastNadir) {
    const pts: Point2D[] = [];
    const edges: Array<(t: number) => [number, number]> = [
      (t) => [-1 + 2 * t, -1],
      (t) => [+1, -1 + 2 * t],
      (t) => [+1 - 2 * t, +1],
      (t) => [-1, +1 - 2 * t],
    ];
    for (const param of edges) {
      for (let i = 0; i < SAMPLES_PER_EDGE; i++) {
        const [sH, sV] = param(i / SAMPLES_PER_EDGE);
        pts.push(projectImagePoint(cam, sH, sV, projection).point);
      }
    }
    return { ring: pts, limits: [], degraded: [] };
  }

  const hitsAt = (sH: number, sV: number) => projectImagePoint(cam, sH, sV, projection).hits;

  /** Highest sV in this column that still reaches ground; null if none does. */
  const topSvFor = (sH: number): number | null => {
    // The bottom ray is the most downward one in the column. If even it misses,
    // nothing above it can land.
    if (!hitsAt(sH, -1)) return null;
    if (hitsAt(sH, +1)) return 1;
    let lo = -1; // known to hit
    let hi = 1;  // known to miss
    for (let i = 0; i < 14; i++) {
      const mid = (lo + hi) / 2;
      if (hitsAt(sH, mid)) lo = mid;
      else hi = mid;
    }
    return lo;
  };

  const leftTop = topSvFor(-1);
  const rightTop = topSvFor(+1);
  const straddles = leftTop !== 1 || rightTop !== 1;
  // Give the far boundary more resolution exactly where it curves: when part of
  // the frame is sky, its shape is driven by the horizon or the hillside, not
  // by the frame.
  const topCols = straddles ? SAMPLES_PER_EDGE * 4 : SAMPLES_PER_EDGE;

  const points: Point2D[] = [];
  let anyGrounded = false;
  const push = (sH: number, sV: number) => {
    const hit = projectImagePoint(cam, sH, sV, projection);
    if (hit.hits) anyGrounded = true;
    points.push(hit.point);
  };

  // Bottom edge, left → right (the near boundary).
  for (let i = 0; i < SAMPLES_PER_EDGE; i++) {
    push(-1 + (2 * i) / SAMPLES_PER_EDGE, -1);
  }
  // Right edge, bottom → this column's last grounded ray.
  if (rightTop !== null) {
    const span = rightTop - -1;
    for (let i = 0; i < SAMPLES_PER_EDGE; i++) {
      push(+1, -1 + (span * i) / SAMPLES_PER_EDGE);
    }
  }
  // Far boundary, right → left, each column at its own last grounded ray.
  for (let i = 0; i < topCols; i++) {
    const sH = +1 - (2 * i) / topCols;
    const top = topSvFor(sH);
    if (top === null) continue; // sky column: contributes no far point
    push(sH, top);
  }
  // Left edge, this column's last grounded ray → bottom.
  if (leftTop !== null) {
    const span = leftTop - -1;
    for (let i = 0; i < SAMPLES_PER_EDGE; i++) {
      push(-1, leftTop - (span * i) / SAMPLES_PER_EDGE);
    }
  }

  // Aimed entirely above the horizon, with no terrain rising into the frustum:
  // there is no ground coverage, so return none.
  //
  // Without this, a camera aimed up drew a full-range fan built entirely from
  // those virtual far points — a plan claiming 29 m of detection for an
  // installation that cannot see the ground at any distance. The virtual point
  // is the right answer for a SINGLE ray (it keeps a near-level cone from
  // degenerating when only its upper edge clears the horizon) and the wrong
  // answer when it is all the polygon is made of.
  //
  // The angle test, not `!anyGrounded` alone. A 180° fisheye aimed straight
  // down has its whole image BORDER on the horizon while its interior sees
  // everything beneath it, and this function only samples the border — so
  // "no sample hit" is true there and must not blank the footprint.
  // `tilt + vfov/2` is the lower edge: it stays positive for that fisheye
  // (90 + 90) and for any level camera, and goes negative only when the frame
  // genuinely sits above the horizon.
  //
  // `anyGrounded` is kept as the terrain escape hatch: ground that rises into
  // an upward frustum reports a real hit, and that coverage is exactly what the
  // sloped-site case needs to keep.
  const lowerEdgeDeg = cam.tiltDeg + cam.vfovDeg / 2;
  if (lowerEdgeDeg <= 0 && !anyGrounded) {
    return {
      ring: [],
      limits: [{ kind: 'frame', atM: Infinity }],
      degraded: [{ reason: 'frame is entirely above the horizon and no terrain rises into it' }],
    };
  }

  return { ring: points, limits: [], degraded: [] };
}



/**
 * The camera's ground coverage, with everything needed to defend the claim.
 *
 * Provenance is read from the terrain model rather than invented: the DEM
 * carries its provider, dataset label, fetch date and grid spacing, and
 * `measuredTerrainReachM` says how far that data actually extends from this
 * camera. Past that edge every elevation silently became the site's own height,
 * so `assumedBeyondM` is the distance past which this answer stops being
 * measured and starts being an assumption.
 *
 * `regions` is at most one entry today. Terrain can genuinely split visible
 * ground into disjoint bands behind a ridge, and expressing that is what the
 * plural shape is for, but the sampler does not detect the split yet. Until it
 * does, a single ring spanning a hidden band is a known over-claim, recorded
 * here rather than left for someone to rediscover.
 */
export function computeCoverage(
  cam: CameraFrustum,
  projection: LensProjection = defaultLensProjection(cam.hfovDeg),
  opts: { configured?: boolean } = {},
): CoverageResult {
  const outcome = buildFootprint(cam, projection);
  const terrainOn = cam.terrain?.enabled === true;
  const prov = terrainProvenanceAt(cam.terrain, cam.lat ?? 0, cam.lng ?? 0);
  const reach = prov.measuredReachM;

  const provenance: CoverageProvenance = {
    elevation: {
      source: prov.source,
      resolutionM: prov.resolutionM,
      datasetVersion: prov.datasetVersion,
    },
    // Only a limit that the camera can actually reach past is worth reporting.
    assumedBeyondM: Number.isFinite(reach) && reach < cam.renderRangeM ? reach : null,
    refraction: { model: 'optical-k0.13', effectiveRadiusM: EFFECTIVE_EARTH_R_M },
  };

  const limits: CoverageLimit[] = [...outcome.limits];
  if (outcome.ring.length > 0) {
    let far = 0;
    for (const p of outcome.ring) {
      far = Math.max(far, Math.hypot(p.x - cam.x, p.y - cam.y) / cam.pixelsPerMeter);
    }

    // What stopped the cone is read from the GEOMETRY, not guessed back from
    // the radius. Inferring "this looks close to the horizon, call it the
    // horizon" needs a fudge factor, and the first one tried (98 %) misread a
    // genuine horizon limit at 7216 m against a 7654 m horizon, because the
    // bisection that finds the last grounded ray stops a little short of
    // tangency by construction.
    //
    // The frame's upper edge either clears the horizon or it does not, and
    // curvedGroundDistanceM already answers exactly that by returning null.
    const upperEdgeDeg = cam.tiltDeg - cam.vfovDeg / 2;
    const upperEscapes =
      upperEdgeDeg <= 0 ||
      curvedGroundDistanceM(cam.h, Math.tan((upperEdgeDeg * Math.PI) / 180)) === null;
    const horizonM = cam.h > 0 ? Math.sqrt(2 * EFFECTIVE_EARTH_R_M * cam.h) : 0;

    // Order matters and the first draft got it wrong: it reported a horizon at
    // 3827 m for a camera whose range is 400 m. A limit that sits BEYOND the
    // range can never be what stopped the cone, so the reachable ones are
    // tested first and the horizon only when it is actually the nearer wall.
    // `far` is a GROUND distance, so the range has to be compared as one:
    // √(R²−h²), not R. Comparing against R made a cone that fully reached its
    // range look 0.5 % short of it and get reported as a frame or horizon
    // limit instead. See `groundReachM`.
    const rangeOnGroundM = groundReachM(cam.renderRangeM, cam.h);
    const reachableCapM = Math.min(rangeOnGroundM, horizonM > 0 ? horizonM : Infinity);
    // 1 % of slack, from measurement rather than taste, in two parts: the far
    // boundary is the maximum over SAMPLED points, which lands a cone at about
    // 99.75 % of its range, and with terrain on the line-of-sight march adds
    // its own discretisation because it searches out to the same cap — the same
    // 400 m scene measured 99.93 % at one cap and 99.50 % at a cap 2 cm
    // shorter. At 99.5 % that second case missed by a centimetre and was
    // reported as a horizon limit 7.6 km further out.
    if (far >= rangeOnGroundM * 0.99) {
      limits.push({ kind: 'range', atM: rangeOnGroundM });
    } else if (terrainOn && far < reachableCapM * 0.98) {
      // Something on the ground stopped it well short of every other wall.
      limits.push({ kind: 'terrain', atM: far });
    } else if (upperEscapes && horizonM > 0 && horizonM < rangeOnGroundM) {
      // Rays past the tangent never land, so the far boundary IS the horizon —
      // but only when the horizon is actually the NEARER wall. This module's
      // own rule, three branches up: a limit sitting beyond the range can never
      // be what stopped the cone. The branch used to fire without checking, so
      // a 400 m cone could be reported as horizon-limited at 7.6 km.
      limits.push({ kind: 'horizon', atM: horizonM });
    } else {
      limits.push({ kind: 'frame', atM: far });
    }
  }

  return {
    regions: outcome.ring.length > 0 ? [{ outer: outcome.ring, holes: [] }] : [],
    configured: opts.configured ?? true,
    pixelsPerMeter: cam.pixelsPerMeter,
    provenance,
    limits,
    degraded: outcome.degraded,
  };
}

/**
 * @deprecated Adapter kept so consumers migrate one at a time. Returns exactly
 * the ring `computeCoverage` produced, which is what every existing test
 * asserts on, so this step changes no drawing anywhere.
 */
export function computeGroundFootprint(
  cam: CameraFrustum,
  projection: LensProjection = defaultLensProjection(cam.hfovDeg),
): Point2D[] {
  if (!PERF) return buildFootprint(cam, projection).ring;
  return pstage('computeGroundFootprint', () => buildFootprint(cam, projection).ring);
}

/**
 * Distance to the NEAR edge of the footprint along the camera's own axis, in
 * metres, or Infinity when the bottom of the frame reaches no ground at all.
 *
 * This is the terrain-aware partner of `nearBlindZoneM`, which solves the same
 * question on a flat plane and therefore answers Infinity for any camera aimed
 * above horizontal. On a slope that is wrong and, worse, visibly wrong: the
 * panel read "blind spot ∞" while the map beside it drew coverage on the
 * hillside. Same question, two answers.
 *
 * Uses the bottom-centre ray, which is the frame's nearest, through the same
 * `rayGroundHit` the footprint is built from — so the number and the drawing
 * cannot disagree.
 */
export function nearGroundDistanceM(cam: CameraFrustum): number {
  const hit = projectImagePoint(cam, 0, -1, 'rectilinear');
  if (!hit.hits) return Infinity;
  return Math.hypot(hit.point.x - cam.x, hit.point.y - cam.y) / cam.pixelsPerMeter;
}

/** Convert a polygon (Point2D[]) into an SVG path string ready to drop
 *  into a `<path d="...">`. Closes back to the first point.
 *  Empty input returns empty string (defensive — caller should guard).
 */
export function polygonToSvgPath(pts: Point2D[]): string {
  if (pts.length < 3) return '';
  let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
  for (let i = 1; i < pts.length; i++) {
    d += ` L ${pts[i].x.toFixed(2)} ${pts[i].y.toFixed(2)}`;
  }
  d += ' Z';
  return d;
}

/** Convenience: compute footprint AND its SVG path in one call. */
export function computeGroundFootprintPath(
  cam: CameraFrustum,
  projection: LensProjection = defaultLensProjection(cam.hfovDeg),
): { points: Point2D[]; path: string } {
  const points = computeGroundFootprint(cam, projection);
  return { points, path: polygonToSvgPath(points) };
}
